/**
 * Lazy Section Loading using Shopify Section Rendering API
 *
 * Defers rendering of below-fold sections until they come into viewport
 * Dramatically improves TTFB by reducing server-side processing on initial page load
 */

class LazySectionLoader {
  constructor() {
    this.observers = new Map();
    this.loadedSections = new Set();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupLazySections());
    } else {
      this.setupLazySections();
    }
  }

  setupLazySections() {
    // Find all sections marked for lazy loading
    const lazySections = document.querySelectorAll('[data-lazy-section]');

    if (lazySections.length === 0) {
      return;
    }

    console.log(`[Lazy Sections] Found ${lazySections.length} sections to lazy load`);

    // Create Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        // Load when section is 200px from entering viewport
        rootMargin: '200px 0px',
        threshold: 0
      }
    );

    // Observe each lazy section
    lazySections.forEach((section) => {
      const sectionId = section.dataset.sectionId || section.id;
      if (sectionId) {
        observer.observe(section);
        this.observers.set(sectionId, observer);
        console.log(`[Lazy Sections] Observing section: ${sectionId}`);
      }
    });
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const placeholder = entry.target;
        const sectionId = placeholder.dataset.sectionId || placeholder.id;

        // Only load once
        if (this.loadedSections.has(sectionId)) {
          return;
        }

        console.log(`[Lazy Sections] Loading section: ${sectionId}`);

        // Find the parent section wrapper (has id="shopify-section-{sectionId}")
        const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);
        if (sectionWrapper) {
          this.loadSection(sectionWrapper, sectionId);
        } else {
          console.warn(`[Lazy Sections] Could not find section wrapper for: ${sectionId}`);
        }

        // Stop observing this section
        const observer = this.observers.get(sectionId);
        if (observer) {
          observer.unobserve(placeholder);
        }
      }
    });
  }

  async loadSection(sectionElement, sectionId) {
    try {
      // Mark as loading
      sectionElement.classList.add('lazy-section-loading');
      this.loadedSections.add(sectionId);

      // Fetch from shadow template to avoid circular placeholder issue
      // Shadow template (page.lazy.json) has same structure but sections not marked for lazy loading
      // Extract locale from current path to ensure locale-aware section rendering
      const pathname = window.location.pathname;
      const localeMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
      const localePrefix = localeMatch ? `/${localeMatch[1]}` : '';
      const url = `${localePrefix}/pages/lazy?section_id=${sectionId}`;

      console.log(`[Lazy Sections] Fetching: ${url}`);

      // Fetch the section HTML using Section Rendering API
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Create temporary container to parse HTML
      const temp = document.createElement('div');
      temp.innerHTML = html;

      // Find the section content
      const newSection = temp.querySelector(`#shopify-section-${sectionId}`) ||
                         temp.querySelector(`[data-section-id="${sectionId}"]`) ||
                         temp.firstElementChild;

      if (newSection) {
        // Replace placeholder with actual content
        sectionElement.outerHTML = newSection.outerHTML;

        console.log(`[Lazy Sections] ✓ Loaded section: ${sectionId}`);

        // Dispatch custom event for any scripts that need to reinitialize
        document.dispatchEvent(new CustomEvent('lazy-section-loaded', {
          detail: { sectionId, element: newSection }
        }));
      } else {
        console.warn(`[Lazy Sections] Could not find section content for: ${sectionId}`);
      }

      // Remove loading class
      sectionElement.classList.remove('lazy-section-loading');
      sectionElement.classList.add('lazy-section-loaded');

    } catch (error) {
      console.error(`[Lazy Sections] Error loading section ${sectionId}:`, error);
      sectionElement.classList.remove('lazy-section-loading');
      sectionElement.classList.add('lazy-section-error');
    }
  }
}

// Initialize lazy section loader
new LazySectionLoader();
